import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { MDA_OPTIONS } from '../../lib/constants';
import type { FoiRecord } from '../../lib/types';
import { ErrorNote, Field, PageHeader } from '../../ui/components';

export function Foi() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ subject: '', targetMda: '', requesterId: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<FoiRecord | null>(null);
  const [trackRef, setTrackRef] = useState('');
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<FoiRecord>('/api/v1/opengov/foi', {
        subject: form.subject,
        targetMda: form.targetMda,
        ...(form.requesterId.trim() ? { requesterId: form.requesterId.trim() } : {}),
      });
      setSubmitted(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit your request — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const track = (e: FormEvent) => {
    e.preventDefault();
    if (trackRef.trim()) navigate(`/foi/${encodeURIComponent(trackRef.trim())}`);
  };

  const copyRef = async (ref: string) => {
    try {
      await navigator.clipboard.writeText(ref);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API unavailable — silently ignore, the reference is shown on screen either way
    }
  };

  return (
    <div>
      <PageHeader
        title="Freedom of Information"
        subtitle="Submit a request under the Access to Information Act 2018, or track a request you already filed."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-5">
          <h2 className="mb-1 text-base font-bold text-slate-900">Submit a request</h2>
          <p className="mb-4 text-sm text-slate-500">
            You do not need to give your name — anonymous requests are permitted. Government must respond within 21
            days of receipt.
          </p>

          {submitted ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-800">Request received.</p>
              <p className="mt-2 text-sm text-emerald-700">
                Your reference number is your only way to check on this request later — write it down or copy it now.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="rounded-md bg-white px-3 py-2 font-mono text-sm font-bold text-slate-900 ring-1 ring-emerald-200">
                  {submitted.refNo}
                </code>
                <button type="button" className="btn-secondary !px-2.5 !py-1.5 text-xs" onClick={() => copyRef(submitted.refNo)}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="mt-3 text-xs text-emerald-700">
                Statutory response due by {formatDate(submitted.statutoryDueAt)}.
              </p>
              <button
                type="button"
                className="btn-primary mt-4"
                onClick={() => navigate(`/foi/${encodeURIComponent(submitted.refNo)}`)}
              >
                View status
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {error ? <ErrorNote message={error} /> : null}
              <Field label="What information are you requesting?">
                <textarea
                  className="field-input"
                  rows={4}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Describe the records or information you are seeking, as specifically as you can."
                />
              </Field>
              <Field label="Which ministry or department holds this information?">
                <select
                  className="field-input"
                  value={form.targetMda}
                  onChange={(e) => setForm({ ...form, targetMda: e.target.value })}
                >
                  <option value="">Select…</option>
                  {MDA_OPTIONS.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Your name or contact reference (optional — leave blank to stay anonymous)">
                <input
                  className="field-input"
                  value={form.requesterId}
                  onChange={(e) => setForm({ ...form, requesterId: e.target.value })}
                  placeholder="Only needed if you want government to be able to reach you directly"
                />
              </Field>
              <button
                className="btn-primary w-full"
                disabled={busy || form.subject.trim().length < 5 || !form.targetMda}
                onClick={submit}
              >
                {busy ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          )}
        </div>

        <div className="card p-5">
          <h2 className="mb-1 text-base font-bold text-slate-900">Track a request</h2>
          <p className="mb-4 text-sm text-slate-500">
            Already have a reference number? Check its status, or file an appeal once it has been responded to.
          </p>
          <form onSubmit={track} className="flex items-end gap-2">
            <Field label="Reference number" className="flex-1">
              <input
                className="field-input"
                value={trackRef}
                onChange={(e) => setTrackRef(e.target.value)}
                placeholder="e.g. FOI-2026-MOH0001"
              />
            </Field>
            <button className="btn-primary" disabled={!trackRef.trim()}>
              Check status
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
